/**
 * InnerCircleCTA — reusable "Become a Founding Member" button.
 *
 * Self-aware:
 *   - Anonymous user → redirects to /?auth=1 (sign-in modal pre-opens)
 *   - Logged-in non-founder → POSTs /api/payments/inner-circle/checkout,
 *     then window.location = stripe checkout URL
 *   - Already founding member → renders a self-aware "You're already in"
 *     badge instead of a button
 *   - Inner Circle full → button disables with the cap message
 *
 * Live slot counter — calls public /api/payments/inner-circle/slots on mount
 * so the marketing copy reads "3 of 10 seats remaining" automatically.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Crown, ArrowRight, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';

export const InnerCircleCTA = ({
  variant = 'primary',
  size = 'lg',
  showSlots = true,
  testId = 'inner-circle-cta',
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [slots, setSlots] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!showSlots) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/payments/inner-circle/slots`);
        if (!cancelled) setSlots(res.data);
      } catch { /* silent — public endpoint, soft fail */ }
    })();
    return () => { cancelled = true; };
  }, [showSlots]);

  const isFounder = user?.subscription_tier === 'founding' || user?.role === 'admin';

  const handleClick = async () => {
    if (!user) {
      // Pass the intent so HomePage knows to fire checkout after auth completes,
      // not just dump the user on the dashboard. This was a real conversion bug —
      // anonymous "Become a Founding Member" clicks were losing their checkout intent.
      navigate('/?auth=1&intent=inner-circle');
      return;
    }
    if (isFounder) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/payments/inner-circle/checkout`,
        {},
        { withCredentials: true },
      );
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Checkout URL missing — please try again or contact support.');
        setLoading(false);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not start checkout. Try again in a moment.';
      toast.error(msg);
      setLoading(false);
    }
  };

  // Already founding — render a badge, not a button.
  if (isFounder) {
    return (
      <div
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-700 dark:text-emerald-300"
        data-testid={`${testId}-already-founder`}
      >
        <CheckCircle size={18} weight="fill" />
        <span className="text-sm font-semibold">You're already an Inner Circle member</span>
      </div>
    );
  }

  // Inner Circle full — disable button with cap message.
  if (slots?.is_full) {
    return (
      <Button
        size={size}
        variant="outline"
        disabled
        data-testid={`${testId}-full`}
        className="cursor-not-allowed"
      >
        Inner Circle is full ({slots.cap}/{slots.cap})
      </Button>
    );
  }

  const remainingLabel = slots
    ? `· ${slots.remaining} of ${slots.cap} seats left`
    : '';

  return (
    <Button
      size={size}
      onClick={handleClick}
      disabled={loading}
      data-testid={testId}
      className={variant === 'primary'
        ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg'
        : ''
      }
    >
      <Crown size={18} weight="duotone" className="mr-2" />
      {loading ? 'Loading…' : `Become a Founding Member ${remainingLabel}`}
      {!loading && <ArrowRight size={16} className="ml-2" />}
    </Button>
  );
};

export default InnerCircleCTA;
